// Hand-designed puzzles. Each level is built backward from a target solution.
// Above each entry is its full brief: setup, players, golden path, edge cases,
// what could go wrong. The data below has to satisfy the brief.
//
// Mechanic note: each attacker has its own move count drawn on the piece.
// Out of moves on every attacker without a goal → retry. Total budget across
// the team is sum(attackers.moves). par is the planned solution length.

export interface PieceXY {
  x: number;
  y: number;
}

export interface AttackerSpec extends PieceXY {
  moves?: number; // default 1
}

export interface LevelDef {
  id: number;
  par: number;          // 3 stars at par; 2 stars at par+1; 1 star otherwise
  goalWidthPct?: number; // default 0.36
  hint?: string;
  attackers: AttackerSpec[];
  defenders: PieceXY[];
  ballAt: PieceXY;
}

export const LEVELS: LevelDef[] = [
  /**
   * L1 — TUTORIAL
   * Setup: 1 attacker just behind ball, no defenders, full goal (0.4).
   * Players: 1 attacker × 1 move.
   * Golden path: drag back from the player, release. Ball travels to goal.
   * Edge cases: very low power → ball stops short. With no clock, that's
   *   fine — the player tries again with the same move (move not consumed
   *   if they don't actually flick; we only consume on release with > 8px
   *   drag).
   * What could go wrong: nothing. No defenders, wide goal, attacker close to
   *   ball. Tutorial guaranteed.
   */
  {
    id: 1,
    par: 1,
    goalWidthPct: 0.4,
    hint: "Drag back from the player. Release.",
    attackers: [{ x: 0.5, y: 0.62, moves: 1 }],
    defenders: [],
    ballAt: { x: 0.5, y: 0.5 },
  },

  /**
   * L2 — AIM AROUND
   * Setup: 1 attacker behind-and-left of ball, 1 defender directly between
   *   ball and centre of goal. Goal 0.4 — wide enough that an angled shot
   *   misses the defender and still scores.
   * Players: 1 attacker × 2 moves.
   * Golden path: flick attacker on a diagonal line passing through the ball
   *   slightly off-centre. Ball travels at an angle, slipping past the
   *   defender on either side, into the goal.
   * Edge cases: dead-centre flick → ball hits defender, ricochets. Move 2
   *   recovers from new ball position.
   * What could go wrong: nothing critical. Goal is wide; angle tolerance is
   *   generous; second move is a safety net.
   */
  {
    id: 2,
    par: 1,
    goalWidthPct: 0.4,
    hint: "Strike the ball at an angle to slip past.",
    attackers: [{ x: 0.38, y: 0.62, moves: 2 }],
    defenders: [{ x: 0.5, y: 0.32 }],
    ballAt: { x: 0.5, y: 0.5 },
  },

  /**
   * L3 — KNOCK THE KEEPER OFF THE LINE
   * Setup: 2 attackers — one far-left, one near the ball-centre. Ball at
   *   centre. 1 defender (the keeper) sits on the goal line.
   * Players: A1 (far left, 1 move) is the keeper-pusher. A2 (centre-back of
   *   ball, 1 move) is the goal-scorer.
   * Golden path: A1 flicks UP-AND-RIGHT on a line that passes well clear of
   *   the ball, slamming into the keeper and knocking them off the goal
   *   line. A2 then flicks straight up through the ball; ball rolls into
   *   the now-open goal.
   * Edge cases: A1 mis-aimed and hits the ball → ball moves; A2 still has a
   *   shot but harder. A1 fails to push keeper enough → A2's shot might
   *   still squeeze past.
   * What could go wrong: the original version of this puzzle had A1 too far
   *   from the keeper to physically reach. With friction lowered to 0.04
   *   max travel ≈ 422px on a typical phone. A1 at y=0.65 (390px) must
   *   reach keeper at y=0.16 (96px) → 294px gap. Reachable, with margin.
   *   A1's x must be far from ball x so the diagonal doesn't intersect the
   *   ball: A1 at x=0.15, ball at x=0.5, keeper at x=0.5 → line passes
   *   roughly through (0.5, 0.16) which is well above ball's y=0.5.
   */
  {
    id: 3,
    par: 2,
    hint: "First attacker pushes the keeper. Second one scores.",
    attackers: [
      { x: 0.15, y: 0.65, moves: 1 },
      { x: 0.5, y: 0.66, moves: 1 },
    ],
    defenders: [{ x: 0.5, y: 0.16 }],
    ballAt: { x: 0.5, y: 0.5 },
  },

  /**
   * L4 — THREAD THE GAP
   * Setup: 1 attacker behind ball, two defenders mid-pitch leaving a clean
   *   centre lane. Goal 0.36.
   * Players: 1 attacker × 1 move.
   * Golden path: flick straight up through the ball, ball threads between
   *   the two defenders, into goal.
   * Edge cases: slight angle off-centre → ball clips a defender, deflects.
   *   Goal still wide enough that some angles score anyway.
   * What could go wrong: gap might be too narrow. Defenders at x=0.36 and
   *   x=0.64 (centres 0.28 apart). On 380px wide pitch, centres are 106px
   *   apart, defenders are 36px each → edge gap 70px, ball is 20px. Fits
   *   easily.
   */
  {
    id: 4,
    par: 1,
    hint: "Thread the gap.",
    attackers: [{ x: 0.5, y: 0.62, moves: 1 }],
    defenders: [
      { x: 0.36, y: 0.32 },
      { x: 0.64, y: 0.32 },
    ],
    ballAt: { x: 0.5, y: 0.5 },
  },

  /**
   * L5 — BANK SHOT
   * Setup: 1 attacker behind ball. 1 defender directly between ball and
   *   centre of goal. Goal 0.36 — defender doesn't span the full goal so
   *   there's gap on either side, but a straight shot is blocked.
   * Players: 1 attacker × 3 moves (this puzzle wants experimentation).
   * Golden path: flick attacker at an angle so ball travels diagonally,
   *   banks off the side wall, and curves back across into the goal corner
   *   on the far side of the defender.
   * Edge cases: a very tight angle past the defender (without banking) also
   *   scores — that's a 3-star solve in 1 move. Banking is the obvious
   *   move; threading is the clever one. Both rewarded.
   * What could go wrong: ball loses too much speed off the side wall. Ball
   *   restitution = 0.9, frictionAir = 0.03 → ball retains energy well.
   *   Should bank with reasonable power.
   */
  {
    id: 5,
    par: 1,
    hint: "Bank off the side, or thread the gap on the edge.",
    attackers: [{ x: 0.5, y: 0.62, moves: 3 }],
    defenders: [{ x: 0.5, y: 0.3 }],
    ballAt: { x: 0.5, y: 0.5 },
  },

  /**
   * L6 — TWO-STEP CLEAR
   * Setup: 2 attackers behind ball, two defenders close together blocking
   *   centre. Direct thread is too tight (edge gap < ball diameter).
   * Players: A1 (left, 1 move) — nudges left defender outward. A2 (right,
   *   1 move) — threads the now-wider gap.
   * Golden path: A1 flicks UP-AND-LEFT into the left defender, pushing it
   *   sideways. A2 flicks UP through the ball, ball goes through the
   *   widened gap into goal.
   * Edge cases: A1 might hit the ball if the line passes too close. A1 at
   *   x=0.3 → left defender at x=0.42: line from (0.3, 0.65) to (0.42, 0.32)
   *   stays well left of ball at x=0.5. Good.
   * What could go wrong: A1's push isn't strong enough. With max travel
   *   ~422px and A1 at y=0.65 → defender at y=0.32, gap is ~200px. Easy
   *   reach with full power. Defender mass is moderate so it slides.
   */
  {
    id: 6,
    par: 2,
    hint: "First attacker widens the gap. Second one threads.",
    attackers: [
      { x: 0.3, y: 0.65, moves: 1 },
      { x: 0.7, y: 0.65, moves: 1 },
    ],
    defenders: [
      { x: 0.42, y: 0.32 },
      { x: 0.58, y: 0.32 },
    ],
    ballAt: { x: 0.5, y: 0.5 },
  },
];

export const TOTAL_LEVELS = LEVELS.length;

export const movesOf = (a: AttackerSpec) => a.moves ?? 1;

export const totalBudget = (level: LevelDef) =>
  level.attackers.reduce((sum, a) => sum + movesOf(a), 0);

export const starsForShots = (level: LevelDef, shotsUsed: number): number => {
  if (shotsUsed <= level.par) return 3;
  if (shotsUsed <= level.par + 1) return 2;
  return 1;
};
